import { useEffect, useState } from "react";
import { http, formatErr } from "../../lib/api";
import { usePolling } from "../../lib/usePolling";
import { toast } from "sonner";
import { Plus, Phone } from "lucide-react";

const EMPTY = { commercial_name: "", company_name: "", phone: "", email: "", vat: "", address: "", notes: "", next_action: "" };

export default function AgentContacts() {
  const [list, setList] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [blocked, setBlocked] = useState(false);

  const load = async () => {
    try { const { data } = await http.get("/crm/me/contacts"); setList(data); setBlocked(false); }
    catch (e) {
      if (e?.response?.status === 403) { setBlocked(true); setList([]); }
      else toast.error(formatErr(e));
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  usePolling(load, { interval: 10000, enabled: !blocked });

  const on = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await http.post("/crm/me/contacts", form); setForm(EMPTY); setShowForm(false); await load(); toast.success("Contatto creato."); }
    catch (err) { toast.error(formatErr(err)); }
    setBusy(false);
  };

  if (list === null) return <div className="p-6 text-sm text-neutral-500">Caricamento…</div>;
  if (blocked) return (
    <div data-testid="agent-contacts-blocked" className="max-w-2xl mx-auto card-clean p-6 text-center">
      <h1 className="font-serif text-2xl mb-2">Contatti</h1>
      <p className="text-sm text-neutral-600">Il tuo account non è ancora attivo. I contatti saranno disponibili dopo l'attivazione.</p>
    </div>
  );

  return (
    <div data-testid="agent-contacts-page" className="max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-5">
        <h1 className="font-serif text-3xl">I tuoi contatti</h1>
        <button onClick={() => setShowForm((s) => !s)} data-testid="agent-contact-new"
          className="px-4 py-2 rounded-md text-white text-sm flex items-center gap-2" style={{ background: "var(--brand-dark)" }}>
          <Plus className="w-4 h-4" /> Nuovo contatto
        </button>
      </div>

      {showForm && (
        <form onSubmit={create} className="card-clean p-5 mb-5 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm" data-testid="agent-contact-form">
          <F label="Nome commerciale *" value={form.commercial_name} onChange={on("commercial_name")} testid="ac-name" required />
          <F label="Ragione sociale" value={form.company_name} onChange={on("company_name")} testid="ac-company" />
          <F label="Telefono" value={form.phone} onChange={on("phone")} testid="ac-phone" />
          <F label="Email" value={form.email} onChange={on("email")} testid="ac-email" />
          <F label="Partita IVA" value={form.vat} onChange={on("vat")} testid="ac-vat" />
          <F label="Prossima azione" value={form.next_action} onChange={on("next_action")} testid="ac-next" />
          <div className="md:col-span-2">
            <button type="submit" disabled={busy} data-testid="agent-contact-save" className="px-4 py-2 rounded-md text-white text-sm" style={{ background: "var(--brand-primary)" }}>Salva contatto</button>
          </div>
        </form>
      )}

      <div className="card-clean overflow-hidden" data-testid="agent-contacts-list">
        {list.length === 0 ? <div className="p-6 text-sm text-neutral-500">Nessun contatto. Creane uno.</div> : (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[10px] uppercase tracking-widest text-neutral-500 border-b" style={{ borderColor: "var(--border-silver)" }}>
              <th className="px-4 py-3">Nome</th><th className="px-4 py-3 hidden md:table-cell">Azienda</th><th className="px-4 py-3">Telefono</th><th className="px-4 py-3 hidden md:table-cell">Prossima azione</th>
            </tr></thead>
            <tbody>
              {list.map((c) => (
                <tr key={c.id} className="border-b last:border-0" style={{ borderColor: "var(--border-silver)" }} data-testid={`agent-contact-row-${c.id}`}>
                  <td className="px-4 py-3 font-medium">{c.commercial_name}</td>
                  <td className="px-4 py-3 hidden md:table-cell text-neutral-500">{c.company_name || "—"}</td>
                  <td className="px-4 py-3">{c.phone ? <a href={`tel:${c.phone}`} className="inline-flex items-center gap-1 text-[var(--brand-dark)]"><Phone className="w-3 h-3" />{c.phone}</a> : "—"}</td>
                  <td className="px-4 py-3 hidden md:table-cell text-neutral-500">{c.next_action || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function F({ label, value, onChange, testid, required }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">{label}</span>
      <input value={value} onChange={onChange} required={required} data-testid={testid}
        className="w-full px-3 py-2 rounded-md border" style={{ borderColor: "var(--border-silver)" }} />
    </label>
  );
}
